import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';

export default function LoginForm({ onLogin }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [githubLoading, setGithubLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!email || !password) {
      setError('Email and password are required.');
      return;
    }
    setLoading(true);
    const { data, error: authError } = await supabase.auth.signInWithPassword({ email, password });
    setLoading(false);
    if (authError) {
      setError(authError.message);
    } else {
      onLogin && onLogin(data);
    }
  };

  const signInWithGoogle = async () => {
    setGoogleLoading(true);
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
    });
    if (error) {
      setError(error.message);
    } else {
      onLogin && onLogin(data);
    }
    setGoogleLoading(false);
  };

  const signInWithGitHub = async () => {
    setGithubLoading(true);
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'github',
    });
    if (error) {
      setError(error.message);
    } else {
      onLogin && onLogin(data);
    }
    setGithubLoading(false);
  };

  useEffect(() => {
    return () => {
      setError('');
    };
  }, []);

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4">
      <h2 className="text-xl font-bold mb-2 text-center">Login</h2>
      <input
        type="email"
        className="rounded px-4 py-2 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-400"
        placeholder="Email"
        value={email}
        onChange={e => setEmail(e.target.value)}
        autoComplete="email"
        required
      />
      <input
        type="password"
        className="rounded px-4 py-2 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-400"
        placeholder="Password"
        value={password}
        onChange={e => setPassword(e.target.value)}
        autoComplete="current-password"
        required
      />
      {error && <div className="text-red-600 text-sm text-center">{error}</div>}
      <button
        type="submit"
        className="bg-blue-600 text-white rounded py-2 font-semibold hover:bg-blue-700 transition-colors disabled:opacity-60"
        disabled={loading}
      >
        {loading ? 'Logging in...' : 'Login'}
      </button>
      <div className="text-center text-sm mt-2">
        {/* Navigation to register is now handled by LoginPage */}
      </div>
      <div className="relative flex py-2 items-center">
        <div className="flex-grow border-t border-gray-300"></div>
        <span className="flex-shrink mx-4 text-gray-500">Or</span>
        <div className="flex-grow border-t border-gray-300"></div>
      </div>
      <button
        type="button"
        onClick={signInWithGoogle}
        className="bg-white border border-gray-300 text-gray-700 rounded py-2 font-semibold hover:bg-gray-50 transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
        disabled={googleLoading}
      >
        <svg className="w-5 h-5" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M44.5 24c0-1.52-.14-2.98-.4-4.39H24v8.51h11.3c-.49 2.76-1.93 5.1-4.01 6.62v5.5h7.08c4.14-3.82 6.56-9.47 6.56-16.24z" fill="#4285F4"/>
          <path d="M24 45c6.48 0 11.93-2.13 15.89-5.81l-7.08-5.5c-2.15 1.45-4.92 2.3-8.81 2.3-6.77 0-12.5-4.55-14.56-10.68H2.3v5.68C6.24 40.45 14.48 45 24 45z" fill="#34A853"/>
          <path d="M9.44 26.38c-.38-1.13-.6-2.33-.6-3.58s.22-2.45.6-3.58V13.5H2.3C.88 16.37 0 19.99 0 24s.88 7.63 2.3 10.5l7.14-5.12z" fill="#FBBC05"/>
          <path d="M24 8.99c3.45 0 6.55 1.19 9 3.67l6.27-6.27C35.93 2.85 30.48 0 24 0 14.48 0 6.24 4.55 2.3 11.82l7.14 5.68c2.06-6.13 7.79-10.68 14.56-10.68z" fill="#EA4335"/>
        </svg>
        Sign in with Google
      </button>
      <button
        type="button"
        onClick={signInWithGitHub}
        className="bg-gray-800 text-white rounded py-2 font-semibold hover:bg-gray-900 transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
        disabled={githubLoading}
      >
        <svg className="w-5 h-5" viewBox="0 0 16 16" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
          <path fillRule="evenodd" d="M8 0C3.58 0 0 3.58 0 8c0 3.54 2.29 6.53 5.47 7.59.4.07.55-.17.55-.38 0-.19-.01-.82-.01-1.49-2.01.37-2.53-.49-2.69-.94-.09-.23-.48-.94-.82-1.13-.28-.15-.68-.52-.01-.53.63-.01 1.08.58 1.23.82.72 1.21 1.87.87 2.33.66.07-.52.28-.87.51-1.07-1.78-.2-3.64-.89-3.64-3.95 0-.87.31-1.59.82-2.15-.08-.2-.36-1.02.08-2.12 0 0 .67-.21 2.2.82.64-.18 1.32-.27 2-.27.68 0 1.36.09 2 .27 1.53-1.04 2.2-.82 2.2-.82.44 1.1.16 1.92.08 2.12.51.56.82 1.27.82 2.15 0 3.07-1.87 3.75-3.65 3.95.29.25.54.73.54 1.48 0 1.07-.01 1.93-.01 2.2 0 .21.15.46.55.38A8.013 8.013 0 0016 8c0-4.42-3.58-8-8-8z" clipRule="evenodd"/>
        </svg>
        Sign in with GitHub
      </button>
    </form>
  );
} 